import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	apiShareDelSettings,
	apiShareGetSettings,
	apiShareSaveSettings,
} from 'shared/modules/share/api';
import { updateSharesDoc } from 'shared/utils/share';
import { SharingType } from 'shared/_model/sharing/types';
import { ShareAccessType } from 'shared/_model/sharing/ShareDialog/constants';

export type SharePermissions = {
	canEdit: boolean;
	canAddFiles: boolean;
	canRemoveFiles: boolean;
};

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type ShareSettings = {
	/** Empty string = link sharing is off (no share record on the server). */
	shareId: string;
	accessType: ShareAccessType;
	permissions: SharePermissions;
};

const NO_LINK: ShareSettings = {
	shareId: '',
	accessType: ShareAccessType.public,
	permissions: { canEdit: false, canAddFiles: false, canRemoveFiles: false },
};

/** Quiet period after the last permission click before the settings are sent. */
const SAVE_DEBOUNCE_MS = 500;

type Params = {
	documentId: string;
	type: SharingType;
	onChange?: () => void | Promise<void>;
};

/**
 * Autosaving share settings.
 *
 * - Permission toggles update the UI at once and are sent together, debounced.
 * - Link on/off, require-password off and "set password" are sent immediately.
 * - Only one request runs at a time; changes made meanwhile are sent in one follow-up request.
 * - A failed save rolls the UI back to the last state confirmed by the server.
 */
export default function useShareSettings({ documentId, type, onChange }: Params) {
	const dispatch = useDispatch();

	const [loaded, setLoaded] = useState(false);
	const [settings, setSettings] = useState<ShareSettings>(NO_LINK);
	/** UI state of the "Require password" toggle (can be on before a password is actually set). */
	const [requirePassword, setRequirePassword] = useState(false);
	const [status, setStatus] = useState<SaveStatus>('idle');
	const [busy, setBusy] = useState(false);
	const [closing, setClosing] = useState(false);

	const settingsRef = useRef<ShareSettings>(NO_LINK);
	const committedRef = useRef<ShareSettings>(NO_LINK);
	/** Password to send with the next request; `undefined` = keep the existing one. */
	const passwordRef = useRef<string | undefined>(undefined);
	const timerRef = useRef<ReturnType<typeof setTimeout>>();
	const dirtyRef = useRef(false);
	const rerunRef = useRef(false);
	const runPromiseRef = useRef<Promise<boolean> | null>(null);
	/** Something was created, changed or deleted: refresh user docs and the parent on close. */
	const changedRef = useRef(false);

	const apply = useCallback((next: ShareSettings) => {
		settingsRef.current = next;
		setSettings(next);
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const s = await apiShareGetSettings(documentId);
				const loadedSettings: ShareSettings = {
					shareId: s.id,
					accessType:
						s.accessType === ShareAccessType.password
							? ShareAccessType.password
							: ShareAccessType.public,
					permissions: {
						canEdit: !!s.canEdit,
						canAddFiles: !!s.canAddFiles,
						canRemoveFiles: !!s.canRemoveFiles,
					},
				};
				if (!cancelled) {
					committedRef.current = loadedSettings;
					apply(loadedSettings);
					setRequirePassword(loadedSettings.accessType === ShareAccessType.password);
				}
			} catch (e) {
				// 400 "Sharing is not exists": the document is not shared yet
			} finally {
				if (!cancelled) {
					setLoaded(true);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [documentId, apply]);

	/** Sends the current settings. Calls made while a request runs are folded into one follow-up request. */
	const run = useCallback((): Promise<boolean> => {
		if (runPromiseRef.current) {
			rerunRef.current = true;
			return runPromiseRef.current;
		}

		const promise = (async () => {
			setStatus('saving');
			try {
				do {
					rerunRef.current = false;
					dirtyRef.current = false;
					const sent = settingsRef.current;
					const password = sent.accessType === ShareAccessType.password ? passwordRef.current : '';
					passwordRef.current = undefined;
					// TODO(open question): in password mode `password` is omitted when it is not changed.
					// Verify the backend keeps the existing password in that case.
					const { sharingId } = await apiShareSaveSettings({
						documentId,
						type,
						accessType: sent.accessType,
						password,
						...sent.permissions,
					});
					committedRef.current = { ...sent, shareId: sharingId };
					apply({ ...settingsRef.current, shareId: sharingId });
				} while (rerunRef.current);
				changedRef.current = true;
				setStatus('saved');
				return true;
			} catch (e) {
				console.log('Error saving share settings', e);
				passwordRef.current = undefined;
				dirtyRef.current = false;
				rerunRef.current = false;
				apply(committedRef.current);
				setRequirePassword(committedRef.current.accessType === ShareAccessType.password);
				setStatus('error');
				return false;
			}
		})().finally(() => {
			runPromiseRef.current = null;
		});

		runPromiseRef.current = promise;
		return promise;
	}, [documentId, type, apply]);

	const clearTimer = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = undefined;
		}
	}, []);

	const scheduleSave = useCallback(() => {
		dirtyRef.current = true;
		clearTimer();
		timerRef.current = setTimeout(() => {
			timerRef.current = undefined;
			run();
		}, SAVE_DEBOUNCE_MS);
	}, [clearTimer, run]);

	// Do not lose pending changes when the dialog is unmounted without a regular close
	useEffect(
		() => () => {
			clearTimer();
			if (dirtyRef.current) {
				run();
			}
		},
		[clearTimer, run]
	);

	const setPermission = useCallback(
		(key: keyof SharePermissions, value: boolean) => {
			if (!settingsRef.current.shareId) {
				return;
			}
			apply({
				...settingsRef.current,
				permissions: { ...settingsRef.current.permissions, [key]: value },
			});
			scheduleSave();
		},
		[apply, scheduleSave]
	);

	/** Turns link sharing on: creates a public, view-only link. */
	const enable = useCallback(async () => {
		setBusy(true);
		try {
			apply({ ...NO_LINK });
			setRequirePassword(false);
			clearTimer();
			await run();
		} finally {
			setBusy(false);
		}
	}, [apply, clearTimer, run]);

	/** Turns link sharing off: deletes the share record. The caller is responsible for asking for confirmation. */
	const disable = useCallback(async () => {
		setBusy(true);
		try {
			clearTimer();
			dirtyRef.current = false;
			await runPromiseRef.current;
			setStatus('saving');
			await apiShareDelSettings(settingsRef.current.shareId);
			committedRef.current = NO_LINK;
			apply(NO_LINK);
			setRequirePassword(false);
			changedRef.current = true;
			setStatus('saved');
		} catch (e) {
			console.log('Error deleting share settings', e);
			setStatus('error');
		} finally {
			setBusy(false);
		}
	}, [apply, clearTimer]);

	/** "Require password" toggle. Turning it on only reveals the field; nothing is sent until a password is set. */
	const changeRequirePassword = useCallback(
		async (value: boolean) => {
			setRequirePassword(value);
			if (!value && settingsRef.current.accessType === ShareAccessType.password) {
				apply({ ...settingsRef.current, accessType: ShareAccessType.public });
				clearTimer();
				setBusy(true);
				try {
					await run();
				} finally {
					setBusy(false);
				}
			}
		},
		[apply, clearTimer, run]
	);

	/** Sets (or replaces) the password. Resolves `true` when it was saved. */
	const submitPassword = useCallback(
		async (password: string) => {
			passwordRef.current = password;
			apply({ ...settingsRef.current, accessType: ShareAccessType.password });
			clearTimer();
			setBusy(true);
			try {
				return await run();
			} finally {
				setBusy(false);
			}
		},
		[apply, clearTimer, run]
	);

	/** Sends pending changes, waits for running requests, then closes. Stays open if saving failed. */
	const requestClose = useCallback(
		async (handleClose: () => void) => {
			setClosing(true);
			clearTimer();
			let ok = true;
			if (dirtyRef.current) {
				ok = await run();
			} else if (runPromiseRef.current) {
				ok = await runPromiseRef.current;
			}
			if (!ok) {
				setClosing(false);
				return;
			}
			try {
				if (changedRef.current) {
					await dispatch(updateSharesDoc(documentId, type, settingsRef.current.shareId));
					await onChange?.();
				}
			} finally {
				handleClose();
			}
		},
		[clearTimer, run, dispatch, documentId, type, onChange]
	);

	return {
		loaded,
		/** Waiting for the server: all controls are disabled. */
		disabled: !loaded || busy || closing,
		closing,
		status,
		shareId: settings.shareId,
		accessType: settings.accessType,
		permissions: settings.permissions,
		requirePassword,
		setPermission,
		enable,
		disable,
		changeRequirePassword,
		submitPassword,
		requestClose,
	};
}
